import { useState } from 'react';
import { voteResults } from '@vellum/core';
import { app } from '../app.ts';
import { useApp } from '../hooks.ts';

/** Start a vote (whiteboards) — the button in the top bar. */
export function StartVoteButton() {
  const kind = useApp('doc', () => app.pageKind());
  const voting = useApp('ui', () => app.voting);
  const [open, setOpen] = useState(false);
  if (kind !== 'WHITEBOARD' || voting?.active) return null;
  return (
    <div style={{ position: 'relative' }}>
      <button className="btn secondary" title="Start a voting session" onClick={() => setOpen(!open)}>
        🗳 Vote
      </button>
      {open && (
        <form
          className="vote-start"
          onSubmit={(e) => {
            e.preventDefault();
            const f = new FormData(e.currentTarget);
            app.sync?.vote({ action: 'start', pageId: app.editor.pageId, votesPerPerson: Number(f.get('votes')) || 3, title: String(f.get('title') ?? '') });
            setOpen(false);
          }}
        >
          <label>
            What are we voting on?
            <input name="title" placeholder="e.g. Ideas to try next" onKeyDown={(e) => e.stopPropagation()} />
          </label>
          <label>
            Votes per person
            <input name="votes" type="number" min={1} max={99} defaultValue={3} onKeyDown={(e) => e.stopPropagation()} />
          </label>
          <button className="btn primary" type="submit">
            Start voting
          </button>
        </form>
      )}
    </div>
  );
}

/** While a vote runs: votes left and the end button. Afterwards: the results. */
export function VotingBar() {
  const v = useApp(['ui', 'doc'], () => app.voting);
  if (!v || v.pageId !== app.editor.pageId) return null;
  if (v.active) {
    const mine = v.votes.filter((x) => x.voter === app.userName).length;
    return (
      <div className="vote-bar" onPointerDown={(e) => e.stopPropagation()}>
        <b>🗳 {v.title}</b>
        <span>
          {v.votesPerPerson - mine} of {v.votesPerPerson} votes left · click layers to vote, ⌥-click to take one back
        </span>
        {v.startedBy === app.userName && (
          <button className="btn primary" onClick={() => app.sync?.vote({ action: 'end' })}>
            End voting
          </button>
        )}
      </div>
    );
  }
  const results = voteResults(v);
  const label = (id: string) => {
    const n = app.doc.get(id);
    return (n?.characters || n?.name || 'Layer').slice(0, 40);
  };
  return (
    <div className="vote-results" onPointerDown={(e) => e.stopPropagation()}>
      <header>
        <b>Results · {v.title}</b>
        <button className="icon-btn" title="Clear the vote" onClick={() => app.sync?.vote({ action: 'clear' })}>
          ✕
        </button>
      </header>
      {results.length === 0 && <div style={{ color: 'var(--text-2)' }}>No votes were cast.</div>}
      {results.map((r, i) => (
        <button
          key={r.nodeId}
          className="vote-row"
          onClick={() => {
            if (!app.doc.has(r.nodeId)) return;
            app.editor.setSelection([r.nodeId]);
            app.zoomToNodes([r.nodeId]);
          }}
        >
          <span className="rank">{i + 1}</span>
          <span style={{ flex: 1, textAlign: 'left' }}>{label(r.nodeId)}</span>
          <b>{r.count}</b>
        </button>
      ))}
      <div style={{ color: 'var(--text-2)', marginTop: 4 }}>{(() => {
          const n = new Set(v.votes.map((x) => x.voter)).size;
          return `${n} ${n === 1 ? 'person' : 'people'} voted`;
        })()}</div>
    </div>
  );
}
